import React, { useState, useEffect } from "react";
import {
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Box,
  Button,
  TextField,
  Grid,
  IconButton,
  Typography,
  Stack,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { Delete, PictureAsPdf, AddCircleOutline } from "@mui/icons-material";
import { Form, Formik, FieldArray } from "formik";
import * as Yup from "yup";
import { LoadingButton } from "@mui/lab";
import { useStyles } from "../../../../Theme/Style";
import { useNotification } from "../../../../Contexts/GlobalNotificationContext";
import {
  useSubmitHeatTransferMutation,
  useGetHeatTransferQuery,
} from "../../../../services/api/production-order";
import axios from "axios";

// ======== Validation ========

const logoItemSchema = Yup.object({
  position: Yup.string().required("Logo position is required"),
  width: Yup.number()
    .typeError("Width must be a number")
    .required("Width is required"),
  height: Yup.number()
    .typeError("Height must be a number")
    .required("Height is required"),
  // If logo is optional, remove .required()
  logo: Yup.mixed().nullable(),
});

const validationSchema = Yup.object({
  logos: Yup.array().of(logoItemSchema).min(1),
});

// Map API → form item
const buildLogoFromSrc = (src = {}) => ({
  position: src.position || "",
  width: src.width || "",
  height: src.height || "",
  logo: src.logo || null, // path on server
  logo_url: src.logo_url || null, // full URL for preview
});

// ======== Component ========

const HeatTransferComponent = ({ prodId }) => {
  const classes = useStyles();
  const { addNotification } = useNotification();

  const [submitHeatTransfer] = useSubmitHeatTransferMutation();
  const { data: embroideryData = [] } = useGetHeatTransferQuery({ id: prodId });

  const [initialValues, setInitialValues] = useState({
    logos: [buildLogoFromSrc()],
  });

  // Initialize from existing data
  useEffect(() => {
    if (Array.isArray(embroideryData) && embroideryData.length > 0) {
      setInitialValues({
        logos: embroideryData.map((item) => buildLogoFromSrc(item)),
      });
    }
  }, [embroideryData]);

  const HeatTransferFileUpload = async (file) => {
    const formData = new FormData();
    formData.append("file", file);
    
    try {
      const response = await axios.post('/file/heat-transfer', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
      return response.data; // { path, url } or whatever your API returns
    } catch (error) {
      console.error('Upload error:', error);
      throw error;
    }
  };

  const HeatTransferFileChange = async (e, index, setFieldValue) => {
    const file = e.target.files?.[0];
    if (!file) return;

    try {
      const res = await HeatTransferFileUpload(file);
      
      // Handle response - adjust based on your actual API response structure
      const logoPath = res.path || res.data?.path || res.file_path;
      const logoUrl = res.url || res.data?.url || res.file_url;

      setFieldValue(`logos[${index}].logo`, logoPath);
      setFieldValue(`logos[${index}].logo_url`, logoUrl);

    } catch (err) {
      console.error('Upload error:', err); // Debug log
      addNotification({ message: "Upload error", type: "error" });
    }
  };

  const onSubmitData = async (values, { setSubmitting }) => {
    try {
      const payloadLogos = values.logos.map((logo) => ({
        position: logo.position,
        width: logo.width,
        height: logo.height,
        logo: logo.logo, // server path
        production_order_id: prodId,
      }));

      const data = {
        production_order_id: prodId,
        logos: payloadLogos,
      };

      await submitHeatTransfer({ data }).unwrap();
      addNotification({ message: "Submitted", type: "success" });
    } catch (e) {
      addNotification({ message: "Error", type: "error" });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Accordion className={classes.accordion}>
      <AccordionSummary
        expandIcon={<ExpandMoreIcon sx={{ color: "#fff" }} />}
        className={classes.accordionSummary}
      >
        <Grid container className={classes.accordionSummaryTextContainer}>
          <Grid item xs={6}>
            Heat Transfer
          </Grid>
          <Grid item xs={6}>
            标志
          </Grid>
        </Grid>
      </AccordionSummary>

      <AccordionDetails className={classes.accordionDetails}>
        <Formik
          initialValues={initialValues}
          enableReinitialize
          validationSchema={validationSchema}
          onSubmit={onSubmitData}
        >
          {({ values, errors, touched, setFieldValue, isSubmitting }) => (
            <Form>
              <FieldArray name="logos">
                {({ push, remove }) => (
                  <>
                    {values.logos.map((logo, index) => {
                      const itemErrors =
                        (errors.logos && errors.logos[index]) || {};
                      const itemTouched =
                        (touched.logos && touched.logos[index]) || {};

                      // Fix: Use logo_url for preview, not the path
                      const logoLink = logo.logo_url || null;
                      const logoFileName = logo.logo
                        ? logo.logo.split("/").pop()
                        : null;

                      return (
                        <Box
                          key={index}
                          sx={{
                            mb: 3,
                            p: 2,
                            borderRadius: 1,
                            border: "1px solid #c0c0c0",
                          }}
                        >
                          {/* Header: "Logo1 Position – Text Field" style */}
                          <Stack
                            direction="row"
                            justifyContent="space-between"
                            alignItems="center"
                            mb={2}
                          >
                            <Typography variant="subtitle1">
                              {`Logo ${index + 1} Position`}
                            </Typography>

                            {values.logos.length > 1 && (
                              <IconButton
                                size="small"
                                onClick={() => remove(index)}
                              >
                                <Delete fontSize="small" />
                              </IconButton>
                            )}
                          </Stack>

                          <Grid container spacing={2}>
                            {/* Top row: position text field (full width) */}
                            <Grid item xs={12}>
                              <TextField
                                fullWidth
                                label={`Logo ${index + 1} Position`}
                                value={logo.position}
                                onChange={(e) =>
                                  setFieldValue(
                                    `logos[${index}].position`,
                                    e.target.value
                                  )
                                }
                                error={
                                  itemTouched.position &&
                                  Boolean(itemErrors.position)
                                }
                                helperText={
                                  itemTouched.position && itemErrors.position
                                }
                              />
                            </Grid>

                            {/* Bottom row: Width / Height / Logo PDF upload */}
                            <Grid item xs={4}>
                              <TextField
                                fullWidth
                                type="number"
                                label="Width"
                                value={logo.width}
                                onChange={(e) =>
                                  setFieldValue(
                                    `logos[${index}].width`,
                                    e.target.value
                                  )
                                }
                                error={
                                  itemTouched.width &&
                                  Boolean(itemErrors.width)
                                }
                                helperText={
                                  itemTouched.width && itemErrors.width
                                }
                              />
                            </Grid>

                            <Grid item xs={4}>
                              <TextField
                                fullWidth
                                type="number"
                                label="Height"
                                value={logo.height}
                                onChange={(e) =>
                                  setFieldValue(
                                    `logos[${index}].height`,
                                    e.target.value
                                  )
                                }
                                error={
                                  itemTouched.height &&
                                  Boolean(itemErrors.height)
                                }
                                helperText={
                                  itemTouched.height && itemErrors.height
                                }
                              />
                            </Grid>

                            <Grid item xs={4}>
                              <input
                                accept="application/pdf"
                                style={{ display: "none" }}
                                id={`heat-pdf-${index}`}
                                type="file"
                                onChange={(e) =>
                                  HeatTransferFileChange(
                                    e,
                                    index,
                                    setFieldValue
                                  )
                                }
                              />
                              <label
                                htmlFor={`heat-pdf-${index}`}
                                style={{ width: "100%" }}
                              >
                                <Button
                                  startIcon={<PictureAsPdf />}
                                  component="span"
                                  fullWidth
                                  variant="outlined"
                                >
                                  {logoFileName
                                    ? logoFileName
                                    : "Logo File – PDF upload"}
                                </Button>
                              </label>

                              {logoLink && (
                                <Box mt={1}>
                                  <Button
                                    size="small"
                                    variant="text"
                                    href={logoLink}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                  >
                                    Preview
                                  </Button>
                                </Box>
                              )}
                            </Grid>
                          </Grid>
                        </Box>
                      );
                    })}

                    {/* Add New button BELOW all logo blocks */}
                    <Box mb={3}>
                      <Button
                        variant="outlined"
                        startIcon={<AddCircleOutline />}
                        onClick={() =>
                          push(
                            buildLogoFromSrc() // empty logo block
                          )
                        }
                      >
                        Add New
                      </Button>
                    </Box>
                  </>
                )}
              </FieldArray>

              <Box mt={2}>
                <LoadingButton
                  variant="contained"
                  type="submit"
                  loading={isSubmitting}
                >
                  Save
                </LoadingButton>
              </Box>
            </Form>
          )}
        </Formik>
      </AccordionDetails>
    </Accordion>
  );
};

export default HeatTransferComponent;
